import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import playerReducer from './slices/playerSlice';
import playlistReducer from './slices/playlistSlice';
import preferencesReducer from './slices/preferencesSlice';
import authReducer from './slices/authSlice';
import themeReducer from './slices/themeSlice';
import type { PlayerState, UserPreferences } from '../types';
import type { ThemeState } from './slices/themeSlice';
import type { PlaylistState } from './slices/playlistSlice';
import type { AuthState } from './slices/authSlice';
import type { PersistPartial } from 'redux-persist/es/persistReducer';
import { combineReducers } from 'redux';
import thunk from 'redux-thunk';
import logger from '../utils/logger';
import { loadChannelsLocally } from '../utils/localStorageUtils';

// Configure persist for playlist, auth and preferences
const playlistPersistConfig = {
  key: 'playlist',
  storage,
  whitelist: ['channels'] // only persist channels
};

const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['user'] // only persist user data
};

const preferencesPersistConfig = {
  key: 'preferences',
  storage,
  whitelist: ['favorites', 'language'] // persist favorites and language
};

// Create persisted reducers
const persistedPlaylistReducer = persistReducer(playlistPersistConfig, playlistReducer);
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedPreferencesReducer = persistReducer(preferencesPersistConfig, preferencesReducer);

const rootReducer = combineReducers({
  player: playerReducer,
  playlist: persistedPlaylistReducer,
  preferences: persistedPreferencesReducer,
  auth: persistedAuthReducer,
  theme: themeReducer,
});

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'settings', 'theme'], // Only persist these reducers
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable serializable check for Redux Persist
    }).concat(thunk),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

// Define RootState type with proper types for each slice
export type RootState = {
  player: PlayerState;
  playlist: PlaylistState & PersistPartial;
  preferences: UserPreferences & PersistPartial;
  auth: AuthState & PersistPartial;
  theme: ThemeState;
};

export type AppDispatch = typeof store.dispatch;

// Load local channels into Redux store after store is created
const preloadChannels = () => {
  try {
    const localChannels = loadChannelsLocally();
    if (localChannels.length > 0) {
      logger.info('Loaded channels from local storage:', { count: localChannels.length });
      store.dispatch({ type: 'playlist/setChannels', payload: localChannels });
    }
  } catch (error) {
    logger.error('Failed to load channels from local storage', error);
  }
};

// Call this after store is created
preloadChannels();

export default store;
